import type { FormattedCitation } from "../../types/active-citations"

import { CheckCircle2, Circle } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ContextSearchMatchPill } from "@/features/chat/components/context-search-match-pill"
import { getContextSearchMatch } from "@/features/chat/utils/context-search-match"
import { cn } from "@/lib/utils"
import { useNavigatorStore } from "@/stores/navigator-store"

import { ActiveCitationsDropdown } from "./active-citations-dropdown"

type CitationCheckboxesProps = {
  libraryCitations: FormattedCitation[]
  libraryName: string
  searchQuery?: string
  sessionType: string
}

export function CitationCheckboxes({
  libraryCitations,
  libraryName,
  searchQuery = "",
  sessionType,
}: CitationCheckboxesProps) {
  const navigatorStore = useNavigatorStore(sessionType)
  const activeCitations = navigatorStore((state) => state.activeCitations)
  const toggleActiveCitations = navigatorStore((state) => state.toggleActiveCitations)

  return (
    <section className="border-sidebar-border border-l pl-3">
      <div className="flex flex-col gap-1.5">
        {libraryCitations.map((citation) => {
          const isActive = activeCitations.includes(citation.fullName)
          const searchMatch = getContextSearchMatch(searchQuery, [
            { label: "library", value: libraryName },
            { label: "title", value: citation.fileName },
            { label: "citation", value: citation.fullName },
            { label: "content", value: citation.searchableText },
          ])
          const selectionButtonClassName = cn(
            "h-7 overflow-hidden p-0 transition-[margin,max-width,opacity]",
            isActive
              ? "ml-1 max-w-7 opacity-100 text-primary"
              : "ml-0 max-w-0 opacity-0 text-muted-foreground group-hover/citation:ml-1 group-hover/citation:max-w-7 group-hover/citation:opacity-100"
          )
          const dropdownWrapperClassName = cn(
            "overflow-hidden transition-[margin,max-width,opacity] pointer-events-none opacity-0 max-w-0",
            "group-hover/citation:pointer-events-auto group-hover/citation:opacity-100 group-hover/citation:max-w-[64px] group-hover/citation:mr-1",
            // On touch devices (no hover capability), always show the dropdown
            "[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:max-w-[64px] [@media(hover:none)]:mr-1"
          )

          return (
            <div
              key={citation.fullName}
              className={cn(
                "group/citation flex items-center gap-2 rounded-md px-2 py-1 transition-colors",
                isActive ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50"
              )}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    aria-pressed={isActive}
                    className="min-w-0 flex-1 text-left text-sm"
                    type="button"
                    onClick={() => toggleActiveCitations([citation.fullName])}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="block min-w-0 flex-1 truncate">{citation.fileName}</span>
                      {searchMatch ? <ContextSearchMatchPill match={searchMatch} /> : null}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{citation.fileName}</p>
                </TooltipContent>
              </Tooltip>
              <div className="flex items-center">
                <div className={dropdownWrapperClassName}>
                  <ActiveCitationsDropdown citation={citation.fullName} />
                </div>
                <Button
                  aria-label={citation.fileName}
                  aria-pressed={isActive}
                  className={selectionButtonClassName}
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={() => toggleActiveCitations([citation.fullName])}
                >
                  {isActive ? <CheckCircle2 className="size-4" /> : <Circle className="size-4" />}
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
